import type { AxiosInstance } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type { MutableEntityListItem, ShotDetailsResponse } from "@/types/project";
import { getErrorMessage, type MutableEntityReorderBody, throwLockAwareError } from "@/api/apiClient";

/** Request body for shot POST — same as GET payload but never includes `lock` (see rest-api/readme.md). */
export type ShotUpdateBody = Omit<ShotDetailsResponse, "lock">;

type ProjectShotIds = Readonly<{ projectId: string; shotId: string }>;

// --- Storyboard shots API — rest-api/readme.md § Storyboard shots API ---

const fetchShotList = async (axiosClient: AxiosInstance, projectId: string): Promise<readonly MutableEntityListItem[]> => {
  try {
    const { data } = await axiosClient.get<readonly MutableEntityListItem[]>(`/${encodeURIComponent(projectId)}/shot`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing storyboard shots."));
  }
};

export const fetchShot = async (axiosClient: AxiosInstance, projectId: string, shotId: string): Promise<ShotDetailsResponse> => {
  try {
    const { data } = await axiosClient.get<ShotDetailsResponse>(`/${encodeURIComponent(projectId)}/shot/${encodeURIComponent(shotId)}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading storyboard shot."));
  }
};

const updateShot = async (axiosClient: AxiosInstance, projectId: string, shotId: string, body: ShotUpdateBody): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.post<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/shot/${encodeURIComponent(shotId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when updating storyboard shot.");
  }
};

const deleteShot = async (axiosClient: AxiosInstance, projectId: string, shotId: string): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.delete<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/shot/${encodeURIComponent(shotId)}`);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when deleting storyboard shot.");
  }
};

const reorderShot = async (
  axiosClient: AxiosInstance,
  projectId: string,
  shotId: string,
  body: MutableEntityReorderBody
): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.patch<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/shot/${encodeURIComponent(shotId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when reordering storyboard shot.");
  }
};

// ===== React Query hooks =====

export const useShotList = (projectId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["shots", projectId],
    queryFn: async () => fetchShotList(axiosClient, projectId!),
    enabled: typeof projectId === "string" && projectId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useShot = (projectId: string | null, shotId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["shot", projectId, shotId],
    queryFn: async () => fetchShot(axiosClient, projectId!, shotId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof shotId === "string" && shotId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateShot = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly shotId: string; readonly body: ShotUpdateBody }) {
      return updateShot(axiosClient, vars.projectId, vars.shotId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["shot", vars.projectId, vars.shotId] });
      void queryClient.invalidateQueries({ queryKey: ["shots", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useDeleteShot = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ProjectShotIds) => deleteShot(axiosClient, vars.projectId, vars.shotId),
    onSuccess(_, vars) {
      void queryClient.removeQueries({ queryKey: ["shot", vars.projectId, vars.shotId] });
      void queryClient.invalidateQueries({ queryKey: ["shots", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useReorderShot = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly shotId: string; readonly body: MutableEntityReorderBody }) {
      return reorderShot(axiosClient, vars.projectId, vars.shotId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["shot", vars.projectId, vars.shotId] });
      void queryClient.invalidateQueries({ queryKey: ["shots", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};
